import React from 'react';
import { useTranslation } from 'react-i18next';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import { GroupMemberDto } from 'legacy/groups/types';
import { useChangeMemberRole, useRemoveMember } from 'legacy/groups/hooks';
import * as toast from 'legacy/common/utils/toast';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

type MoreOptionsProps = {
  organizationId: string;
  closeMemberOptionsModal: () => void;
  visible: boolean;
  selectedMember: GroupMemberDto | null;
  handleAssignTitle?: (memberId: string) => void;
};

const MemberOptionsModal: React.FC<MoreOptionsProps> = ({
  organizationId,
  closeMemberOptionsModal,
  visible,
  selectedMember,
  handleAssignTitle
}) => {
  const { t } = useTranslation(['organizations']);
  const { mutate: updateMemberStatus } = useChangeMemberRole();
  const { mutate: removeMember } = useRemoveMember();
  const flag = useFlags();

  const removeFromOrganization = () => {
    removeMember(
      {
        groupId: organizationId,
        userId: selectedMember.user.id
      },
      {
        onSuccess: () => {
          toast.success(
            t('errors.memberRemovedSuccess', {
              firstName: selectedMember.user.firstName,
              lastName: selectedMember.user.lastName
            })
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(t('errors.memberRemovalError'));
        }
      }
    );
  };

  const makeAdmin = () => {
    updateMemberStatus(
      {
        groupId: organizationId,
        userId: selectedMember.user.id,
        role: 'admin'
      },
      {
        onSuccess: () => {
          toast.success(
            t('success.memberMadeAdmin', {
              firstName: selectedMember.user.firstName,
              lastName: selectedMember.user.lastName
            })
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(t('memberActions.memberMadeAdminError'));
        }
      }
    );
  };

  const removeAsAdmin = () => {
    updateMemberStatus(
      {
        groupId: organizationId,
        userId: selectedMember.user.id,
        role: 'member'
      },
      {
        onSuccess: () => {
          toast.success(
            t('success.memberRemoved', {
              firstName: selectedMember.user.firstName,
              lastName: selectedMember.user.lastName
            })
          );
          closeMemberOptionsModal();
        },
        onError: () => {
          toast.error(t('memberActions.memberRemovalError'));
        }
      }
    );
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={closeMemberOptionsModal}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        {selectedMember && (
          <div className="flex flex-col gap-4">
            {handleAssignTitle && flag[FLAGS.ENABLE_ORGANIZATION_TITLES] && (
              <Button
                type={ButtonType.Inverted}
                newRadius
                onClick={() => handleAssignTitle(selectedMember.user.id)}
              >
                {t('memberActions.setTitle')}
              </Button>
            )}
            <Button
              type={ButtonType.Danger}
              newRadius
              onClick={removeFromOrganization}
            >
              {t('memberActions.removeFromOrg')}
            </Button>
            {selectedMember?.role === 'admin' && (
              <Button
                type={ButtonType.Danger}
                newRadius
                onClick={removeAsAdmin}
              >
                {t('memberActions.removeAsAdmin')}
              </Button>
            )}
            {selectedMember?.role === 'member' && (
              <Button type={ButtonType.Inverted} newRadius onClick={makeAdmin}>
                {t('memberActions.makeAdmin')}
              </Button>
            )}
          </div>
        )}
        <Divider size={4} />
        <div className="flex gap-2 flex-col pb-2 mt-6">
          <Button type={ButtonType.Inverted} onClick={closeMemberOptionsModal}>
            {t('actions.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MemberOptionsModal;
